import { AvailableDevices, MediaDeviceInfoType, SelectedDevices, State } from '../types/Video';
import { ReactComponent as MicrophoneNotFoundSvg } from 'assets/icons/microphoneNotFound.svg';
import { ReactComponent as MicrophoneMutedSvg } from 'assets/icons/microphoneMuted.svg';
import { ReactComponent as CameraNotFound } from 'assets/icons/cameraNotFound.svg';
import { ReactComponent as RotateCameraSvg } from 'assets/icons/arrowRepeat.svg';
import { ReactComponent as CameraMutedSvg } from 'assets/icons/cameraMuted.svg';
import { ReactComponent as MicrophoneSvg } from 'assets/icons/microhpone.svg';
import { ReactComponent as SettingsSvg } from 'assets/icons/settings.svg';
import { ReactComponent as DialPadSvg } from 'assets/icons/dialPad.svg';
import { ReactComponent as PersonSvg } from 'assets/icons/person.svg';
import { ReactComponent as CameraSvg } from 'assets/icons/camera.svg';
import { ReactComponent as PhoneSvg } from 'assets/icons/phone.svg';
import { ReactComponent as BlurSvg } from 'assets/icons/blur.svg';
import { logger } from 'utils/logger';

import { PermissionWarning } from '../shared/PermissionWarning';
import DeviceDropdown from '../shared/DeviceDropdown';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { poweredByLink } from '../shared/variables';
import { Room } from '@telnyx/video';
import {
  SettingsModalTitle,
  RotateCameraButton,
  SettingsButton,
  SettingsModal,
  InCallWrapper,
  ToggleButton,
  DialPadModal,
  RemoteVideo,
  BlurButton,
  LocalVideo,
  Disconnect,
  PoweredBy,
  Controls,
  Content,
  Middle,
  Center,
  Right,
  Media,
  Left,
} from './InCallUpgradeStyle';

type InCallUpgradeProps = {
  changeCurrentState: (state: State) => void;
  localVideoTrack: MediaStreamTrack | null;
  localAudioTrack: MediaStreamTrack | null;
  remoteVideoTrack: MediaStreamTrack | null;
  remoteAudioTrack: MediaStreamTrack | null;
  activeCall: any;
  room: Room | null;
  microphoneMuted: boolean;
  cameraMuted: boolean;
  toggleMicrophone: () => void;
  toggleCamera: () => void;
  updateDevice: (device: MediaDeviceInfoType) => void;
  availableDevices?: AvailableDevices;
  selectedDevices?: SelectedDevices;
  isVideoBlurred: boolean;
  applyBlurOnInCallVideo: () => void;
  TurnCameraOnInCallVideo: () => void;
  sendFinalStats: () => Promise<void>;
  cameraPermissionDenied?: boolean;
};

export const InCallUpgrade = ({
  changeCurrentState,
  localVideoTrack,
  localAudioTrack,
  remoteVideoTrack,
  remoteAudioTrack,
  activeCall,
  room,
  microphoneMuted,
  cameraMuted,
  toggleMicrophone,
  toggleCamera,
  updateDevice,
  availableDevices,
  selectedDevices,
  isVideoBlurred,
  applyBlurOnInCallVideo,
  TurnCameraOnInCallVideo,
  sendFinalStats,
  cameraPermissionDenied,
}: InCallUpgradeProps) => {
  const [dialPadInputValue, setDialPadInputValue] = useState<string>('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDialPadModal, setShowDialPadModal] = useState(false);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const { t } = useTranslation();
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const settingsModalRef = useRef<HTMLDivElement>(null);
  const dialPadModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (localVideoRef.current) {
      const mediaStream = new MediaStream();

      if (localVideoTrack) {
        mediaStream.addTrack(localVideoTrack);
        logger.info('Local video track added to media stream', 'InCallUpgrade', {
          trackId: room?.id || 'unknown',
          enabled: localVideoTrack.enabled
        });
      }

      if (localAudioTrack) {
        mediaStream.addTrack(localAudioTrack);
        logger.info('Local audio track added to media stream', 'InCallUpgrade', {
          trackId: room?.id || 'unknown',
          enabled: localAudioTrack.enabled
        });
      }

      if (mediaStream.getTracks().length > 0) {
        localVideoRef.current.srcObject = mediaStream;
        localVideoRef.current.play().catch((err) => {
          console.error('Error playing video:', err);
          logger.error('Error playing local video', 'InCallUpgrade', {
            error: err.message,
            stackTrace: err.stack
          });
        });
        logger.info('Local video stream started playing', 'InCallUpgrade', {
          trackCount: mediaStream.getTracks().length
        });
      } else {
        localVideoRef.current.srcObject = null;
        if (!cameraMuted && !microphoneMuted) {
          console.warn('No tracks available to display - check camera/microphone permissions');
          logger.warn('No media tracks available for display', 'InCallUpgrade', {
            cameraMuted,
            microphoneMuted
          });
        }
      }
    }
  }, [localVideoTrack, localAudioTrack]);

  useEffect(() => {
    console.log('New remoteVideoTrack', remoteVideoTrack);
    if (remoteVideoRef.current && remoteVideoTrack) {
      const mediaStream = new MediaStream([remoteVideoTrack]);
      remoteVideoRef.current.srcObject = mediaStream;
      remoteVideoRef.current.play();
      setShowDialPadModal(false);
      
      logger.info('Remote video track received and started playing', 'InCallUpgrade', {
        trackId: room?.id || 'unknown',
        enabled: remoteVideoTrack.enabled
      });
    }
  }, [remoteVideoTrack]);

  const handleDisconnect = async () => {
    logger.info('User initiated disconnect', 'InCallUpgrade', {
      hasActiveCall: !!activeCall,
      hasRoom: !!room
    });

    if (activeCall) {
      activeCall.hangup();
      logger.info('Active call hangup completed', 'InCallUpgrade');
    }
    if (room) {
      await room.disconnect();
      logger.info('Room disconnect completed', 'InCallUpgrade');
    }
    try {
      await sendFinalStats();
      logger.info('Final stats sent successfully', 'InCallUpgrade');
    } catch (error) {
      console.error('Error sending final stats:', error);
      logger.error('Error sending final stats', 'InCallUpgrade', {
        error: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : undefined
      });
    }
    
    changeCurrentState('postCall');
  };

  const handleDeviceSelect = (device: MediaDeviceInfoType) => {
    logger.info('Device selected', 'InCallUpgrade', {
      deviceKind: device.kind,
      deviceLabel: device.label,
      deviceId: device.deviceId
    });
    setShowSettingsModal(false);
    updateDevice(device);
  };

  useEffect(() => {
    const handleClick = (event: any) => {
      if (settingsModalRef.current && !settingsModalRef.current.contains(event.target)) {
        setShowSettingsModal(false);
      }
      if (dialPadModalRef.current && !dialPadModalRef.current.contains(event.target)) {
        setShowDialPadModal(false);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [settingsModalRef, dialPadModalRef]);

  const generateDialPad = () => {
    const dialItems = [];

    for (let i = 1; i <= 9; i++) {
      dialItems.push(
        <button onClick={() => handleDialPadItemClick(i.toString())} key={i}>
          {i}
        </button>,
      );
    }

    dialItems.push(
      <button onClick={() => handleDialPadItemClick('*')} key="*">
        *
      </button>,
      <button onClick={() => handleDialPadItemClick('0')} key="0">
        0
      </button>,
      <button onClick={() => handleDialPadItemClick('#')} key="#">
        #
      </button>,
    );

    return dialItems;
  };

  const handleDialPadItemClick = (item: string) => {
    logger.info('Dial pad item clicked', 'InCallUpgrade', {
      item,
      currentInput: dialPadInputValue,
      newInput: dialPadInputValue + item
    });
    setDialPadInputValue(dialPadInputValue + item);
    activeCall?.dtmf(item);
  };

  useEffect(() => {
    if (isVideoBlurred) {
      applyBlurOnInCallVideo();
    }
  }, []);

  return (
    <InCallWrapper>
      <Center>
        <Content>
          <Media>
            <RemoteVideo>
              <video ref={remoteVideoRef} autoPlay playsInline={true} muted={false} />

              {!remoteVideoTrack && <PersonSvg />}
            </RemoteVideo>
            <LocalVideo $isVideoBlurred={isVideoBlurred}>
              <div>
                <video ref={localVideoRef} autoPlay playsInline={true} muted={true} id={'localVideoInCall'} />
                <canvas id="localVideoInCallCanvas" className={'hide'}></canvas>
                {!localVideoTrack?.enabled && <PersonSvg />}
              </div>
              <PermissionWarning show={cameraPermissionDenied} />
            </LocalVideo>
            <BlurButton onClick={() => {
              logger.info('Blur button clicked', 'InCallUpgrade', {
                currentBlurState: isVideoBlurred
              });
              applyBlurOnInCallVideo();
            }} $isVideoBlurred={isVideoBlurred}>
              <BlurSvg />
            </BlurButton>
            <RotateCameraButton onClick={() => {
              logger.info('Rotate camera button clicked', 'InCallUpgrade');
              TurnCameraOnInCallVideo();
            }}>
              <RotateCameraSvg />
            </RotateCameraButton>
          </Media>
          <Controls>
            <Left></Left>
            <Middle>
              <ToggleButton
                $muted={showDialPadModal}
                onClick={(e) => {
                  e.stopPropagation();
                  logger.info('Dial pad button clicked', 'InCallUpgrade', {
                    currentState: showDialPadModal,
                    newState: !showDialPadModal
                  });
                  setShowDialPadModal(!showDialPadModal);
                  setShowSettingsModal(false);
                }}
              >
                <DialPadSvg />
              </ToggleButton>
              <ToggleButton $muted={microphoneMuted} disabled={true} style={{ cursor: 'not-allowed' }}>
                  <MicrophoneMutedSvg />
              </ToggleButton>
              <Disconnect onClick={() => handleDisconnect()}>
                <PhoneSvg />
              </Disconnect>
              <ToggleButton $muted={cameraMuted} onClick={() => {
                logger.info('Camera toggle button clicked', 'InCallUpgrade', {
                  currentState: cameraMuted,
                  newState: !cameraMuted,
                  availableVideoDevices: availableDevices?.videoInputs.length || 0
                });
                toggleCamera();
              }}>
                {availableDevices?.videoInputs.length === 0 ? (
                  <CameraNotFound />
                ) : cameraMuted ? (
                  <CameraMutedSvg />
                ) : (
                  <CameraSvg />
                )}
              </ToggleButton>
              <SettingsButton>
                <ToggleButton
                  $muted={showSettingsModal}
                  onClick={(e) => {
                    e.stopPropagation();
                    logger.info('Settings button clicked', 'InCallUpgrade', {
                      currentState: showSettingsModal,
                      newState: !showSettingsModal
                    });
                    setShowSettingsModal(!showSettingsModal);
                    setShowDialPadModal(false);
                  }}
                >
                  <SettingsSvg />
                </ToggleButton>
              </SettingsButton>
              {showSettingsModal && (
                <SettingsModal ref={settingsModalRef}>
                  <SettingsModalTitle>{t('inCall.settings')}</SettingsModalTitle>
                  {availableDevices?.audioOutputs && (
                    <DeviceDropdown
                      availableDevices={availableDevices.audioInputs}
                      selectedDevice={selectedDevices?.audioInput}
                      label={t('devices.microphone')}
                      onSelect={handleDeviceSelect}
                    />
                  )}
                  {availableDevices?.audioOutputs && (
                    <DeviceDropdown
                      availableDevices={availableDevices.audioOutputs}
                      selectedDevice={selectedDevices?.audioOutput}
                      label={t('devices.speaker')}
                      onSelect={handleDeviceSelect}
                    />
                  )}
                  {availableDevices?.audioOutputs && (
                    <DeviceDropdown
                      availableDevices={availableDevices.videoInputs}
                      selectedDevice={selectedDevices?.videoInput}
                      label={t('devices.camera')}
                      onSelect={handleDeviceSelect}
                    />
                  )}
                </SettingsModal>
              )}
              {showDialPadModal && (
                <DialPadModal ref={dialPadModalRef}>
                  <div>
                    <input value={dialPadInputValue} />
                  </div>
                  {generateDialPad()}
                </DialPadModal>
              )}
            </Middle>
            <Right $isVideoBlurred={isVideoBlurred}>
              <BlurButton onClick={() => {
                logger.info('Right blur button clicked', 'InCallUpgrade', {
                  currentBlurState: isVideoBlurred
                });
                applyBlurOnInCallVideo();
              }} $isVideoBlurred={isVideoBlurred}>
                <BlurSvg />
              </BlurButton>
            </Right>
          </Controls>
          <PoweredBy>
            <a href={poweredByLink} target="_blank" aria-label={`${t('common.poweredBy')} ${t('common.opensInNewTab')}`}>
              {t('common.poweredBy')}
              <span className="sr-only">{t('common.opensInNewTab')}</span>
            </a>
          </PoweredBy>
        </Content>
      </Center>
    </InCallWrapper>
  );
};
